import React, { memo, useMemo, useState } from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import type { TimetableGrid } from '../../services/timetableService';
import { DAY_NAMES, gridColumns, orderedDays, periodsOn } from './timetableUtils';
import type { DropInfo } from './board/boardModel';

export interface GridItem {
  id: string;
  day: number;
  period: number;
  span: 1 | 2;
  node: React.ReactNode;
}

interface WeekGridProps {
  grid: TimetableGrid;
  items: GridItem[];
  /** While a card is dragged: what dropping on each cell would do, keyed `day:period`. */
  drops?: Map<string, DropInfo> | null;
  onDrop?: (day: number, period: number, occupantId?: string) => void;
  /** Day to accent, e.g. today on My Timetable. */
  today?: number;
  minCellWidth?: number;
}

const cellKey = (day: number, period: number) => `${day}:${period}`;

/**
 * A week as days down the side and periods across, with breaks as slim
 * columns and the end of a short day hatched. Lesson cards are passed in;
 * while dragging, cells take the colour of what a drop would do there.
 */
const WeekGrid = memo<WeekGridProps>(({ grid, items, drops, onDrop, today, minCellWidth = 112 }) => {
  const theme = useTheme();
  const [hover, setHover] = useState<string | null>(null);
  const columns = useMemo(() => gridColumns(grid), [grid]);
  const days = useMemo(() => orderedDays(grid), [grid]);
  const template = `76px ${columns.map((c) => (c.kind === 'period' ? `minmax(${minCellWidth}px, 1fr)` : '18px')).join(' ')}`;
  const starts = useMemo(() => new Map(items.map((i) => [cellKey(i.day, i.period), i])), [items]);
  const coveredBy = useMemo(() => {
    const m = new Map<string, GridItem>();
    items.forEach((i) => { if (i.span === 2) m.set(cellKey(i.day, i.period + 1), i); });
    return m;
  }, [items]);

  const tone = (info?: DropInfo) => {
    if (!info) return {};
    const { success, warning, primary, error, text } = theme.palette;
    switch (info.kind) {
      case 'ok': return { bgcolor: alpha(success.main, 0.08), outline: `1.5px dashed ${alpha(success.main, 0.55)}` };
      case 'prefer': return { bgcolor: alpha(warning.main, 0.1), outline: `1.5px dashed ${alpha(warning.main, 0.6)}` };
      case 'swap': return { outline: `2px dashed ${alpha(primary.main, 0.7)}` };
      case 'blocked': return { bgcolor: alpha(error.main, 0.05) };
      default: return { bgcolor: alpha(text.primary, 0.03) };
    }
  };

  const dropHandlers = (day: number, period: number, occupantId?: string) => {
    if (!drops || !onDrop) return {};
    const key = cellKey(day, period);
    const info = drops.get(key);
    const canDrop = info && (info.kind === 'ok' || info.kind === 'prefer' || info.kind === 'swap');
    return {
      onDragOver: (e: React.DragEvent) => { if (canDrop) e.preventDefault(); if (hover !== key) setHover(key); },
      onDragLeave: () => setHover((h) => (h === key ? null : h)),
      onDrop: (e: React.DragEvent) => { e.preventDefault(); setHover(null); if (canDrop) onDrop(day, period, occupantId); },
    };
  };

  const hatch = `repeating-linear-gradient(45deg, transparent, transparent 5px, ${alpha(theme.palette.text.primary, 0.035)} 5px, ${alpha(theme.palette.text.primary, 0.035)} 10px)`;

  return (
    <Box sx={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: template, minWidth: 'min-content' }}>
        {/* Header */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />
        {columns.map((c, i) => (c.kind === 'period' ? (
          <Box key={`h${i}`} sx={{ px: 1.25, py: 1, borderBottom: '1px solid', borderLeft: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.3 }}>{c.label || `Period ${c.period}`}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>{c.start}–{c.end}</Typography>
          </Box>
        ) : (
          <Box key={`h${i}`} sx={{ borderBottom: '1px solid', borderLeft: '1px solid', borderColor: 'divider', bgcolor: alpha(theme.palette.text.primary, 0.03) }} />
        )))}

        {/* Days */}
        {days.map((day, row) => {
          const last = periodsOn(grid, day);
          const isLast = row === days.length - 1;
          const border = { borderBottom: isLast ? 0 : '1px solid', borderLeft: '1px solid', borderColor: 'divider' };
          return (
            <React.Fragment key={day}>
              <Box sx={{
                px: 1.5, display: 'flex', alignItems: 'center', borderBottom: isLast ? 0 : '1px solid', borderColor: 'divider',
                bgcolor: today === day ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
              }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: today === day ? 'primary.main' : 'text.primary' }}>
                  {DAY_NAMES[day].slice(0, 3)}
                </Typography>
              </Box>
              {columns.map((c, i) => {
                if (c.kind === 'break') {
                  return (
                    <Box key={i} sx={{ ...border, bgcolor: alpha(theme.palette.text.primary, 0.03), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {row === 0 && (
                        <Typography variant="caption" sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: '0.6rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.disabled' }}>
                          {c.label}
                        </Typography>
                      )}
                    </Box>
                  );
                }
                const key = cellKey(day, c.period);
                if (coveredBy.has(key)) return null; // The second half of a double period.
                const item = starts.get(key);
                const off = c.period > last;
                const info = drops?.get(key);
                const showReason = hover === key && info?.reason && (info.kind === 'blocked' || info.kind === 'prefer' || info.kind === 'off');
                return (
                  <Box
                    key={i}
                    {...dropHandlers(day, c.period, item?.id)}
                    sx={{
                      ...border,
                      // A double never crosses a break, so its second period is the next column.
                      gridColumn: item?.span === 2 ? 'span 2' : undefined,
                      p: 0.5,
                      minHeight: 64,
                      position: 'relative',
                      background: off ? hatch : undefined,
                      transition: 'background-color 100ms',
                    }}
                  >
                    {!off && (
                      <Box sx={{ height: '100%', borderRadius: '8px', outlineOffset: -2, ...tone(info) }}>
                        {item?.node}
                      </Box>
                    )}
                    {showReason && (
                      <Typography
                        variant="caption"
                        sx={{
                          position: 'absolute', left: 4, right: 4, bottom: 4, zIndex: 1, px: 0.75, py: 0.4, borderRadius: '6px',
                          fontSize: '0.64rem', lineHeight: 1.25, pointerEvents: 'none', fontWeight: 600,
                          color: info!.kind === 'prefer' ? 'warning.dark' : 'error.main',
                          // Over a card the reason needs its own backing to stay readable.
                          bgcolor: 'background.paper',
                          boxShadow: `0 1px 4px ${alpha(theme.palette.common.black, 0.12)}`,
                        }}
                      >
                        {info!.reason}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
});

WeekGrid.displayName = 'WeekGrid';

export default WeekGrid;
